import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || '';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Helper to get full URL for uploaded files
// Uses window.location.origin to always use the current domain
export const getUploadUrl = (path) => {
  if (!path) return '';
  // If already a full URL with current origin, return as is
  if (path.startsWith('http://') || path.startsWith('https://')) {
    // Check if it's an old URL with wrong domain, fix it
    if (path.includes('/api/uploads/')) {
      const relativePath = path.substring(path.indexOf('/api/uploads/'));
      return `${window.location.origin}${relativePath}`;
    }
    return path;
  }
  // Use current browser origin for relative paths
  return `${window.location.origin}${path}`;
};

// Upload API - saves relative paths only
export const uploadAPI = {
  image: async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post('/api/upload/image', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    // Keep relative URL - don't add base URL
    return response;
  },
  document: async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post('/api/upload/document', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    // Keep relative URL - don't add base URL
    return response;
  },
  cv: async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post('/api/upload/cv', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    // Keep relative URL - don't add base URL
    return response;
  },
};

// Products API (NO MORE PRICE)
export const productsAPI = {
  getAll: () => api.get('/api/products'),
  getOne: (id) => api.get(`/api/products/${id}`),
  create: (data) => api.post('/api/products', data),
  update: (id, data) => api.put(`/api/products/${id}`, data),
  delete: (id) => api.delete(`/api/products/${id}`),
};

// Contacts API
export const contactsAPI = {
  getAll: () => api.get('/api/contacts'),
  create: (data) => api.post('/api/contacts', data),
  delete: (id) => api.delete(`/api/contacts/${id}`),
};

// Team API
export const teamAPI = {
  getAll: () => api.get('/api/team'),
  create: (data) => api.post('/api/team', data),
  update: (id, data) => api.put(`/api/team/${id}`, data),
  delete: (id) => api.delete(`/api/team/${id}`),
  reorder: (memberIds) => api.put('/api/team/reorder', { member_ids: memberIds }),
};

// Blog API
export const blogAPI = {
  getAll: () => api.get('/api/blog'),
  create: (data) => api.post('/api/blog', data),
  update: (id, data) => api.put(`/api/blog/${id}`, data),
  delete: (id) => api.delete(`/api/blog/${id}`),
};

// Certifications API
export const certificationsAPI = {
  getAll: () => api.get('/api/certifications'),
  create: (data) => api.post('/api/certifications', data),
  update: (id, data) => api.put(`/api/certifications/${id}`, data),
  delete: (id) => api.delete(`/api/certifications/${id}`),
};

// Testimonials API
export const testimonialsAPI = {
  getAll: () => api.get('/api/testimonials'),
  create: (data) => api.post('/api/testimonials', data),
  update: (id, data) => api.put(`/api/testimonials/${id}`, data),
  delete: (id) => api.delete(`/api/testimonials/${id}`),
};

// Portfolio API
export const portfolioAPI = {
  getAll: () => api.get('/api/portfolio'),
  create: (data) => api.post('/api/portfolio', data),
  update: (id, data) => api.put(`/api/portfolio/${id}`, data),
  delete: (id) => api.delete(`/api/portfolio/${id}`),
};

// Compliance API
export const complianceAPI = {
  getAll: () => api.get('/api/compliance'),
  getByType: (type) => api.get(`/api/compliance/${type}`),
  create: (data) => api.post('/api/compliance', data),
  update: (type, data) => api.put(`/api/compliance/${type}`, data),
};

// DPO Contact API
export const dpoContactAPI = {
  getAll: () => api.get('/api/dpo-contacts'),
  create: (data) => api.post('/api/dpo-contacts', data),
  delete: (id) => api.delete(`/api/dpo-contacts/${id}`),
};

// Job Applications API
export const jobApplicationsAPI = {
  getAll: () => api.get('/api/job-applications'),
  create: (data, cvUrl) => api.post(`/api/job-applications?cv_url=${encodeURIComponent(cvUrl)}`, data),
  getCvUrl: (id) => api.get(`/api/job-applications/${id}/cv-url`),
  delete: (id) => api.delete(`/api/job-applications/${id}`),
};

// Home Content API
export const homeContentAPI = {
  get: () => api.get('/api/home-content'),
  update: (data) => api.put('/api/home-content', data),
};

// SMTP Settings API
export const smtpAPI = {
  get: () => api.get('/api/settings/smtp'),
  update: (data) => api.put('/api/settings/smtp', data),
  test: (email) => api.post(`/api/settings/smtp/test?test_email=${email}`),
};

// Data retention API
export const retentionAPI = {
  getSettings: () => api.get('/api/retention/settings'),
  updateSettings: (data) => api.put('/api/retention/settings', data),
  preview: () => api.get('/api/retention/preview'),
  cleanup: () => api.post('/api/retention/cleanup'),
};

// Manutenzione / messa in produzione (admin)
export const maintenanceAPI = {
  run: (task, mode) => api.post(`/api/admin/maintenance/run/${task}?mode=${mode}`),
  complianceBackup: () => api.get('/api/admin/maintenance/compliance-backup'),
  backups: () => api.get('/api/admin/maintenance/compliance-backups'),
  restore: (snapshotId) => api.post(`/api/admin/maintenance/restore-compliance/${snapshotId}`),
  runs: () => api.get('/api/admin/maintenance/runs'),
};

// Stato dei documenti PDF referenziati (admin)
export const documentsHealthAPI = {
  check: () => api.get('/api/admin/documents-health'),
};

// Responsabili Whistleblowing (OdV / custodi) API
export const responsabiliAPI = {
  getAll: () => api.get('/api/whistleblowing/responsabili'),
  create: (data) => api.post('/api/whistleblowing/responsabili', data),
  delete: (id) => api.delete(`/api/whistleblowing/responsabili/${id}`),
};

// Auth API
export const authAPI = {
  login: (data) => api.post('/api/auth/login', data),
  register: (data) => api.post('/api/auth/register', data),
  me: () => api.get('/api/auth/me'),
  check: () => api.get('/api/auth/check'),
  changePassword: (data) => api.post('/api/auth/change-password', data),
};

// Blocked IPs API
export const blockedIPsAPI = {
  getAll: () => api.get('/api/auth/blocked-ips'),
  unblock: (ip) => api.delete(`/api/auth/blocked-ips/${encodeURIComponent(ip)}`),
  clearAll: () => api.delete('/api/auth/blocked-ips'),
};

// Custom Pages API
export const pagesAPI = {
  getAll: () => api.get('/api/pages'),
  get: (slug) => api.get(`/api/pages/${slug}`),
};

// Menu Settings API
export const menuSettingsAPI = {
  get: () => api.get('/api/settings/menu'),
  update: (data) => api.put('/api/settings/menu', data),
};

// Footer Settings API
export const footerSettingsAPI = {
  get: () => api.get('/api/settings/footer'),
  update: (data) => api.put('/api/settings/footer', data),
};

// Custom pages API (admin write operations)
export const customPagesAPI = {
  getAll: () => api.get('/api/pages'),
  create: (data) => api.post('/api/pages', data),
  update: (slug, data) => api.put(`/api/pages/${slug}`, data),
  remove: (slug) => api.delete(`/api/pages/${slug}`),
};

// Set auth token for all requests
export const setAuthToken = (token) => {
  if (token) {
    api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    localStorage.setItem('gmed_admin_token', token);
  } else {
    delete api.defaults.headers.common['Authorization'];
    localStorage.removeItem('gmed_admin_token');
  }
};

// Initialize token from localStorage
const savedToken = localStorage.getItem('gmed_admin_token');
if (savedToken) {
  api.defaults.headers.common['Authorization'] = `Bearer ${savedToken}`;
}

// An expired or missing session must be visible: without this an admin save fails silently
api.interceptors.response.use(
  (response) => response,
  (error) => {
    const isAuthCall = (error.config?.url || '').includes('/api/auth/login');
    if (error.response?.status === 401 && !isAuthCall) {
      delete api.defaults.headers.common['Authorization'];
      localStorage.removeItem('gmed_admin_token');
      if (window.location.pathname.startsWith('/admin') && !window.location.pathname.includes('/login')) {
        window.sessionStorage.setItem('gmed_session_expired', '1');
        window.location.href = '/admin/login';
      }
    }
    return Promise.reject(error);
  }
);

export default api;
